import { FormaSwitch } from '@/components/sklad/FormaSwitch';
import {
  type Piece,
  type TimelineEvent,
  buildTimeline,
  formatDateTime,
  formatQty,
  parseQty,
} from '@/components/sklad/types';
import { useEscapeKey } from '@/hooks/useEscapeKey';
import { useMemo, useState } from 'react';

const DOT_COLOR: Record<TimelineEvent['kind'], string> = {
  order: 'bg-primary',
  receive: 'bg-success',
  subtract: 'bg-warning',
  adjust: 'bg-text-muted',
  spent: 'bg-danger',
};

const INPUT =
  'text-sm text-text bg-bg border border-border rounded-lg px-3 py-2 outline-none focus:border-primary focus:ring-2 focus:ring-primary/30 transition-all';

/**
 * Detail jedné role: náhled látky, aktuální stav (inline editovatelný), odečet
 * metráže, označení „zdělaná", ostatní role stejné látky a historie pohybů.
 */
export function RollDetailModal({
  piece,
  siblings,
  busy,
  onOpenSibling,
  onPrint,
  onSetStock,
  onSetFolded,
  onSubtract,
  onMarkSpent,
  onUnmarkSpent,
  onClose,
}: {
  piece: Piece;
  siblings: Piece[];
  busy: boolean;
  onOpenSibling: (p: Piece) => void;
  /** Dotisk štítku role. */
  onPrint: () => void;
  onSetStock: (qty: number) => void;
  onSetFolded: (folded: boolean) => void;
  onSubtract: (amount: number) => void;
  onMarkSpent: () => void;
  onUnmarkSpent: () => void;
  onClose: () => void;
}) {
  useEscapeKey(onClose);
  const [qtyEditing, setQtyEditing] = useState(false);
  const [qtyValue, setQtyValue] = useState('');
  const [subtractOpen, setSubtractOpen] = useState(false);
  const [subtractValue, setSubtractValue] = useState('');

  // Živý náhled stavu po odečtu — ať je hned vidět, že role padne na nulu.
  const preview = useMemo(() => {
    const amount = parseQty(subtractValue);
    if (!Number.isFinite(amount)) return null;
    return Math.max(0, Math.round((piece.stock_qty - amount) * 100) / 100);
  }, [piece.stock_qty, subtractValue]);

  const timeline = useMemo(() => buildTimeline(piece), [piece]);

  function startQtyEdit() {
    setQtyValue(String(piece.stock_qty));
    setSubtractOpen(false);
    setQtyEditing(true);
  }

  function saveQty() {
    const qty = parseQty(qtyValue);
    if (!Number.isFinite(qty) || qty < 0) return;
    onSetStock(Math.round(qty * 100) / 100);
    setQtyEditing(false);
  }

  function confirmSubtract() {
    const amount = parseQty(subtractValue);
    if (!Number.isFinite(amount) || amount <= 0) return;
    onSubtract(amount);
    setSubtractOpen(false);
    setSubtractValue('');
  }

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center p-4 overflow-y-auto">
      <button
        type="button"
        aria-label="Zavřít"
        onClick={onClose}
        className="fixed inset-0 bg-black/30 backdrop-blur-sm cursor-default"
      />
      <div className="relative w-full max-w-lg my-10 bg-surface rounded-2xl shadow-modal p-6 reveal">
        <div className="flex gap-5">
          {piece.latka_image ? (
            <img
              src={piece.latka_image}
              alt=""
              className="w-36 h-36 shrink-0 rounded-lg object-cover border border-border bg-bg"
            />
          ) : (
            <div className="w-36 h-36 shrink-0 rounded-lg bg-bg border border-border" />
          )}
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <span className="text-[11px] font-semibold text-text-muted uppercase tracking-wider">
                Role č. {piece.serial_number}
              </span>
              {piece.spent_at && (
                <span className="text-[11px] font-semibold text-danger bg-danger-light px-1.5 py-0.5 rounded">
                  Zděláno
                </span>
              )}
            </div>
            <h2 className="text-lg font-bold text-text mt-0.5 break-words">{piece.latka_name}</h2>
            {/* Forma materiálu — stejný přepínač jako na příjmu, kdyby se ťuklo vedle. */}
            <div className="mt-2">
              <FormaSwitch
                value={!!piece.folded}
                onChange={onSetFolded}
                disabled={busy || !!piece.spent_at}
              />
            </div>
            <div className="text-xs text-text-muted mt-3">Aktuální množství</div>
            {piece.spent_at || !qtyEditing ? (
              <button
                type="button"
                onClick={piece.spent_at ? undefined : startQtyEdit}
                disabled={!!piece.spent_at}
                title={piece.spent_at ? undefined : 'Upravit stav'}
                className="group inline-flex items-center gap-2 cursor-pointer disabled:cursor-default"
              >
                <span className="text-3xl font-bold text-text leading-tight">
                  {formatQty(piece.stock_qty, piece.unit)}
                </span>
                {!piece.spent_at && (
                  <span className="text-text-muted group-hover:text-primary transition-colors text-sm">
                    ✎
                  </span>
                )}
              </button>
            ) : (
              <div className="flex items-center gap-2 mt-0.5">
                <input
                  type="text"
                  inputMode="decimal"
                  value={qtyValue}
                  onChange={(e) => setQtyValue(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') saveQty();
                    else if (e.key === 'Escape') setQtyEditing(false);
                  }}
                  className={`${INPUT} w-24 text-2xl font-bold`}
                />
                <span className="text-lg text-text-muted">{piece.unit}</span>
                <button
                  type="button"
                  onClick={saveQty}
                  disabled={busy}
                  className="text-action hover:text-action-dark font-bold cursor-pointer disabled:opacity-50"
                >
                  Uložit
                </button>
                <button
                  type="button"
                  onClick={() => setQtyEditing(false)}
                  className="text-text-muted hover:text-danger cursor-pointer"
                >
                  Zrušit
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Akce se stavem */}
        {piece.spent_at ? (
          <div className="mt-4 flex gap-2">
            <button
              type="button"
              onClick={onUnmarkSpent}
              disabled={busy}
              className="text-sm font-semibold text-text-secondary border border-border rounded-lg px-4 py-2.5 hover:bg-surface-hover transition-colors cursor-pointer disabled:opacity-50"
            >
              Vrátit na sklad
            </button>
            <TiskButton onPrint={onPrint} />
          </div>
        ) : (
          !subtractOpen && (
            <div className="flex gap-2 mt-4">
              <button
                type="button"
                onClick={() => {
                  setSubtractOpen(true);
                  setQtyEditing(false);
                }}
                className="flex-1 text-sm font-semibold bg-action hover:bg-action-dark text-white rounded-lg px-4 py-2.5 transition-colors cursor-pointer"
              >
                Odečíst látku
              </button>
              <button
                type="button"
                onClick={onMarkSpent}
                disabled={busy}
                className="flex-1 text-sm font-semibold text-danger border border-danger/40 rounded-lg px-4 py-2.5 hover:bg-danger-light transition-colors cursor-pointer disabled:opacity-50"
              >
                Látka zdělána
              </button>
              <TiskButton onPrint={onPrint} />
            </div>
          )
        )}

        {subtractOpen && (
          <div className="mt-4 pt-4 border-t border-border">
            <span className="text-sm font-medium text-text-secondary block mb-1.5">
              Odečíst látku
            </span>
            <div className="flex items-center gap-3 flex-wrap">
              <div className="flex items-center gap-1.5">
                <input
                  type="text"
                  inputMode="decimal"
                  value={subtractValue}
                  onChange={(e) => setSubtractValue(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') confirmSubtract();
                    else if (e.key === 'Escape') {
                      setSubtractOpen(false);
                      setSubtractValue('');
                    }
                  }}
                  placeholder="Kolik"
                  className={`${INPUT} w-24`}
                />
                <span className="text-sm text-text-muted">{piece.unit}</span>
              </div>
              {preview !== null &&
                (preview === 0 ? (
                  <span className="text-sm font-semibold text-danger">
                    Látka bude kompletně zdělána
                  </span>
                ) : (
                  <span className="text-sm text-text-secondary">
                    Nový stav bude{' '}
                    <strong className="text-text">{formatQty(preview, piece.unit)}</strong>
                  </span>
                ))}
            </div>
            <div className="flex gap-2 mt-3">
              <button
                type="button"
                onClick={confirmSubtract}
                disabled={busy}
                className="flex-1 bg-action hover:bg-action-dark text-white text-sm font-semibold rounded-lg px-4 py-2.5 transition-colors cursor-pointer disabled:opacity-50"
              >
                {busy ? 'Ukládám…' : 'Odečíst'}
              </button>
              <button
                type="button"
                onClick={() => {
                  setSubtractOpen(false);
                  setSubtractValue('');
                }}
                className="flex-1 text-sm font-semibold text-text-secondary border border-border rounded-lg px-4 py-2.5 hover:bg-surface-hover transition-colors cursor-pointer"
              >
                Zrušit
              </button>
            </div>
          </div>
        )}

        {siblings.length > 0 && (
          <div className="mt-5 pt-4 border-t border-border">
            <div className="text-sm font-semibold text-text-secondary mb-2">
              Další role stejné látky
            </div>
            <div className="space-y-0.5">
              {siblings.map((s) => (
                <button
                  key={s.id}
                  type="button"
                  onClick={() => onOpenSibling(s)}
                  className="w-full flex items-center justify-between gap-3 px-3 py-2 rounded-lg hover:bg-surface-hover transition-colors cursor-pointer text-left"
                >
                  <span className="text-sm text-text flex items-center gap-2">
                    Role <strong className="font-semibold">č. {s.serial_number}</strong>
                    {s.spent_at && (
                      <span className="text-[11px] font-semibold text-danger bg-danger-light px-1.5 py-0.5 rounded">
                        zděláno
                      </span>
                    )}
                  </span>
                  <span className="text-sm font-medium text-text-secondary">
                    {formatQty(s.stock_qty, s.unit)}
                  </span>
                </button>
              ))}
            </div>
          </div>
        )}

        <div className="mt-5 pt-4 border-t border-border">
          <div className="text-sm font-semibold text-text-secondary mb-3">Historie</div>
          <ol className="space-y-3">
            {timeline.map((e, i) => (
              <li key={`${e.kind}-${e.time}-${i}`} className="flex gap-3">
                <div className="flex flex-col items-center">
                  <span className={`w-2.5 h-2.5 rounded-full mt-1 shrink-0 ${DOT_COLOR[e.kind]}`} />
                  {i < timeline.length - 1 && <span className="w-px flex-1 bg-border mt-1" />}
                </div>
                <div className="pb-1 min-w-0">
                  <div className="text-sm text-text">{e.title}</div>
                  {e.desc && <div className="text-xs text-text-muted">{e.desc}</div>}
                  <div className="text-[11px] text-text-muted mt-0.5">{formatDateTime(e.time)}</div>
                </div>
              </li>
            ))}
          </ol>
        </div>

        <div className="flex justify-end mt-6">
          <button
            type="button"
            onClick={onClose}
            className="text-sm font-semibold text-text-secondary border border-border rounded-lg px-4 py-2.5 hover:bg-surface-hover transition-colors cursor-pointer"
          >
            Zavřít
          </button>
        </div>
      </div>
    </div>
  );
}

/** Dotisk štítku — stejné tlačítko používá detail role ve všech stavech. */
function TiskButton({ onPrint }: { onPrint: () => void }) {
  return (
    <button
      type="button"
      onClick={onPrint}
      title="Vytisknout štítek role"
      className="inline-flex items-center gap-1.5 text-sm font-semibold text-text-secondary border border-border rounded-lg px-4 py-2.5 hover:bg-surface-hover transition-colors cursor-pointer whitespace-nowrap"
    >
      <svg
        width="16"
        height="16"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
      >
        <polyline points="6 9 6 2 18 2 18 9" />
        <path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2" />
        <rect x="6" y="14" width="12" height="8" />
      </svg>
      Tisknout štítek
    </button>
  );
}
